import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { paiementService } from '../../api/services';
import { Paiement, MODE_PAIEMENT_LABELS, Page } from '../../types';
import { formatTND, formatDate } from '../../utils/formatters';
import { DataTable, Column } from '../../components/common/DataTable';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

export function PaiementList() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState<string>('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const loadPaiements = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, size: 10 };
      if (modeFilter) params.modePaiement = modeFilter;
      if (dateDebut) params.dateDebut = dateDebut;
      if (dateFin) params.dateFin = dateFin;
      const data: Page<Paiement> = await paiementService.getAll(params);
      setPaiements(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      toast.error(t.messages.loadError);
    } finally {
      setLoading(false);
    }
  }, [currentPage, modeFilter, dateDebut, dateFin, t.messages.loadError]);

  useEffect(() => {
    loadPaiements();
  }, [loadPaiements]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await paiementService.delete(deleteId);
      toast.success(t.messages.deleteSuccess);
      loadPaiements();
    } catch (error) {
      toast.error(t.messages.deleteError);
    } finally {
      setDeleteId(null);
    }
  };

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadPaiements();
  };

  const columns: Column<Paiement>[] = [
    {
      key: 'datePaiement',
      header: t.paiements.datePaiement,
      sortable: true,
      render: (p) => (
        <span className="text-sm">{formatDate(p.datePaiement)}</span>
      ),
    },
    {
      key: 'documentNumero',
      header: t.paiements.document,
      render: (p) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/documents/view/${p.documentId}`);
          }}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          {p.documentNumero}
        </button>
      ),
    },
    {
      key: 'clientRaisonSociale',
      header: t.documents.client,
      render: (p) => (
        <span className="text-sm font-medium text-gray-900">{p.clientRaisonSociale}</span>
      ),
    },
    {
      key: 'montant',
      header: t.paiements.montant,
      sortable: true,
      render: (p) => (
        <span className="tnd-amount font-medium text-green-700">{formatTND(p.montant)}</span>
      ),
    },
    {
      key: 'modePaiement',
      header: t.paiements.modePaiement,
      render: (p) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          {MODE_PAIEMENT_LABELS[p.modePaiement]}
        </span>
      ),
    },
    {
      key: 'reference',
      header: t.paiements.reference,
      render: (p) => (
        <span className="text-sm text-gray-600">{p.reference || '-'}</span>
      ),
    },
    {
      key: 'actions',
      header: t.common.actions,
      render: (p) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDeleteId(p.id);
          }}
          className="text-red-600 hover:text-red-800 p-1"
          title={t.common.delete}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t.paiements.title}</h1>
          <p className="page-subtitle">{totalElements} paiements</p>
        </div>
        <button onClick={() => navigate('/paiements/new')} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-1" />
          {t.paiements.newPaiement}
        </button>
      </div>

      <div className="card">
        <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <select
            value={modeFilter}
            onChange={(e) => {
              setModeFilter(e.target.value);
              setCurrentPage(0);
            }}
            className="input-field sm:w-48"
          >
            <option value="">{t.common.all} - {t.paiements.modePaiement}</option>
            {Object.entries(MODE_PAIEMENT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">Du :</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">Au :</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="input-field"
            />
          </div>
          <button type="submit" className="btn-primary">
            {t.common.filter}
          </button>
        </form>
      </div>

      <DataTable
        columns={columns}
        data={paiements}
        totalElements={totalElements}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        loading={loading}
        emptyMessage={t.common.noResults}
      />

      <ConfirmDialog
        isOpen={deleteId !== null}
        title={t.messages.confirmDelete}
        message={t.paiements.deleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

export default PaiementList;
